/**
 * useCurrentMember — v.1-D §7.10, PRD FR-019, Decision D-003.
 *
 * Authoritative role/status authority is the Convex `members` table surfaced
 * via `api.organizations.getCurrentUserSecurity`. The Payload JWT is read for
 * loading-state gating only — NEVER for the role claim.
 *
 * Why: a stale JWT carries a role claim that does not reflect role-revocation
 * until the token expires. v.1-C used the JWT directly, which kept revoked
 * admins authoritative for up-to-an-hour. v.1-D reads the `members` row,
 * which is reactive: a role change propagates to every mounted hook within
 * ~500 ms (Convex push).
 *
 * Dev-mode `window.__currentMember` exposure (§0.4 footnote 5): populated on
 * every reactive change so the Playwright smoke suite (§22.3 V.8) can poll
 * `window.__currentMember.role` to assert role-change propagation. Gated by
 * `import.meta.env.DEV` so production bundles drop the block via tree-shaking.
 */
import { useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { useAuth } from '../lib/payloadAuth';

export type MemberRole = 'admin' | 'editor' | 'viewer';
export type MemberStatus = 'active' | 'invited' | 'suspended';

export interface CurrentMember {
  id: string;
  email: string;
  name: string | null;
  role: MemberRole;
  status: MemberStatus | null;
  lastSeenAt: number | null;
  /** Multi-tenant scaffold — currently the workspace's own principal ID. */
  tenantId: string;
}

interface MemberRowFromSecurity {
  _id: string;
  email?: string | null;
  name?: string | null;
  role: MemberRole;
  status?: MemberStatus | null;
  lastSeenAt?: number | null;
}

interface SecurityQueryShape {
  subject: string;
  email?: string;
  member: MemberRowFromSecurity | null;
}

export function useCurrentMember(): CurrentMember | null | undefined {
  const { user, loading } = useAuth();
  const security = useQuery(
    api.organizations.getCurrentUserSecurity,
    user ? {} : 'skip',
  ) as SecurityQueryShape | undefined;

  let currentMember: CurrentMember | null | undefined;
  if (loading) {
    currentMember = undefined;
  } else if (!user) {
    currentMember = null;
  } else if (security === undefined) {
    currentMember = undefined;
  } else if (!security.member) {
    currentMember = null;
  } else {
    currentMember = {
      id:         security.member._id,
      email:      security.member.email ?? security.email ?? '',
      name:       security.member.name ?? null,
      role:       security.member.role,
      status:     (security.member.status ?? null) as MemberStatus | null,
      lastSeenAt: security.member.lastSeenAt ?? null,
      // Single-tenant: principal ID stands in for tenant ID.
      tenantId:   security.subject,
    };
  }

  // Dev-mode exposure for Playwright smoke V.8. Tree-shaken from prod bundles.
  useEffect(() => {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      (window as unknown as Record<string, unknown>).__currentMember =
        currentMember;
    }
  }, [currentMember]);

  return currentMember;
}

export function useHasRole(role: MemberRole): boolean | undefined {
  const m = useCurrentMember();
  if (m === undefined) return undefined;
  if (m === null) return false;
  return m.role === role;
}

export function useHasMinRole(min: MemberRole): boolean | undefined {
  const m = useCurrentMember();
  if (m === undefined) return undefined;
  if (m === null) return false;
  const rank: Record<MemberRole, number> = { viewer: 0, editor: 1, admin: 2 };
  return rank[m.role] >= rank[min];
}

export function useIsAdmin(): boolean | undefined {
  return useHasRole('admin');
}

export function useTenantId(): string | undefined {
  return useCurrentMember()?.tenantId;
}
